/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useGetDayKwhQuery } from '../../redux/api';
import DatePicker220 from './DatePicker220';
import styles from './DayKwhChart.module.css';

function formatDateForInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const FALLBACK_TARIFF_UAH_PER_KWH = 6.5;

export default function DayKwhChart({ embedded = false, titleId = 'day-kwh-chart-title', onSelectedDateChange } = {}) {
  const { t } = useTranslation();
  const [dateValue, setDateValue] = useState(() => formatDateForInput(new Date()));
  const [hoveredHour, setHoveredHour] = useState(null);
  const [visibleSeries, setVisibleSeries] = useState({
    evCharging: true,
    dam: true,
    mining: false,
  });

  const { data, isFetching, isError, error } = useGetDayKwhQuery({ date: dateValue });
  const isRateLimited = isError && error?.status === 429;

  const raw = data?.hourlyKwh220 ?? [];
  const hourlyKwh = useMemo(() => raw.map(v => Number(v)), [raw]);

  const rawToka = data?.hourlyKwhToka ?? [];
  const hourlyKwhToka = useMemo(
    () => (Array.isArray(rawToka) && rawToka.length === 24 ? rawToka : Array(24).fill(0)).map(v => Number(v)),
    [rawToka]
  );

  const hourlyTariffUahPerKwh = useMemo(() => {
    const tariff220 = data?.hourlyTariff220;
    if (
      Array.isArray(tariff220) &&
      tariff220.length === 24 &&
      tariff220.some(v => v != null && !Number.isNaN(Number(v)))
    ) {
      return tariff220.map(v => (v != null && !Number.isNaN(Number(v)) ? Number(v) : FALLBACK_TARIFF_UAH_PER_KWH));
    }
    return Array(24).fill(FALLBACK_TARIFF_UAH_PER_KWH);
  }, [data?.hourlyTariff220]);

  const hourlyDamUahPerKwh = useMemo(() => {
    const oree = data?.hourlyPriceOree;
    if (Array.isArray(oree) && oree.length === 24) {
      return oree.map(v => {
        const parsed = Number(v);
        return Number.isFinite(parsed) ? parsed / 1000 : null;
      });
    }
    return Array(24).fill(null);
  }, [data?.hourlyPriceOree]);

  const hourlyMiningTariffUahPerKwh = useMemo(() => {
    const mining = data?.hourlyMiningTariff;
    if (Array.isArray(mining) && mining.length === 24) {
      return mining.map(v => {
        const parsed = Number(v);
        return Number.isFinite(parsed) ? parsed : null;
      });
    }
    return Array(24).fill(null);
  }, [data?.hourlyMiningTariff]);

  /** Max of (220 + toka) per hour so stacked bars fit in 100% height */
  const maxKwh = useMemo(() => {
    if (hourlyKwh.length !== 24 || hourlyKwhToka.length !== 24) return 1;
    let m = 0;
    for (let i = 0; i < 24; i++) {
      const sum = Number(hourlyKwh[i] ?? 0) + Number(hourlyKwhToka[i] ?? 0);
      if (sum > m) m = sum;
    }
    return m > 0 ? m : 1;
  }, [hourlyKwh, hourlyKwhToka]);

  const tariffRange = useMemo(() => {
    const vals = [
      ...(visibleSeries.evCharging ? hourlyTariffUahPerKwh : []),
      ...(visibleSeries.dam ? hourlyDamUahPerKwh : []),
      ...(visibleSeries.mining ? hourlyMiningTariffUahPerKwh : []),
    ].filter(v => v != null && !Number.isNaN(v));
    if (vals.length === 0) return { min: 0, max: 10 };
    const max = Math.max(...vals);
    const padding = Math.max((max - 0) * 0.05, 0.5);
    return { min: 0, max: max + padding };
  }, [hourlyTariffUahPerKwh, hourlyDamUahPerKwh, hourlyMiningTariffUahPerKwh, visibleSeries]);

  const rightAxisTicks = useMemo(() => {
    const step = maxKwh / 4;
    return [0, step, step * 2, step * 3, maxKwh];
  }, [maxKwh]);

  const hasData = hourlyKwh.length === 24 && (hourlyKwh.some(v => v > 0) || hourlyKwhToka.some(v => v > 0));

  /** Tariff line: 0 when total volume at that hour is 0 or negligible, otherwise actual tariff */
  const tariffLineDisplay = useMemo(() => {
    return hourlyTariffUahPerKwh.map((v, i) => {
      const volumeAtHour = Number(hourlyKwh[i] ?? 0) + Number(hourlyKwhToka[i] ?? 0);
      if (volumeAtHour <= 0 || !Number.isFinite(volumeAtHour)) return 0;
      const num = Number(v);
      return v != null && !Number.isNaN(num) ? num : tariffRange.min;
    });
  }, [hourlyKwh, hourlyKwhToka, hourlyTariffUahPerKwh, tariffRange]);

  const damLineDisplay = useMemo(
    () =>
      hourlyDamUahPerKwh.map(v => {
        const num = Number(v);
        return v != null && !Number.isNaN(num) ? num : 0;
      }),
    [hourlyDamUahPerKwh]
  );

  const miningTariffLineDisplay = useMemo(
    () =>
      hourlyMiningTariffUahPerKwh.map(v => {
        const num = Number(v);
        return v != null && !Number.isNaN(num) ? num : 0;
      }),
    [hourlyMiningTariffUahPerKwh]
  );

  /** Range always includes 0; add bottom padding so 0 line is slightly above chart edge (line more visible) */
  const effectiveTariffRange = useMemo(() => {
    const vals = [
      ...(visibleSeries.evCharging ? tariffLineDisplay : []),
      ...(visibleSeries.dam ? damLineDisplay : []),
      ...(visibleSeries.mining ? miningTariffLineDisplay : []),
    ].filter(v => v != null && !Number.isNaN(v));
    if (vals.length === 0) return { min: 0, max: 10 };
    const max = Math.max(...vals);
    if (max <= 0) return { min: 0, max: 10 };
    const padding = Math.max(max * 0.05, 0.5);
    return { min: 0, max: max + padding };
  }, [tariffLineDisplay, damLineDisplay, miningTariffLineDisplay, visibleSeries]);

  /** Bottom inset so 0 tariff line sits above the chart bottom edge (more visible, "more up") */
  const TARIFF_BOTTOM_INSET = 0.08;
  const buildLinePoints = values => {
    const { min, max } = effectiveTariffRange;
    const range = max - min || 1;
    const chartBottom = 100 - TARIFF_BOTTOM_INSET * 100;
    const chartHeight = chartBottom;
    return values
      .map(val => {
        const norm = range > 0 ? (Number(val) - min) / range : 0;
        const y = chartBottom - norm * chartHeight;
        return y;
      })
      .map((y, i) => `${((i + 0.5) / 24) * 100},${y}`)
      .join(' ');
  };

  const tariffLinePoints = useMemo(() => {
    return buildLinePoints(tariffLineDisplay);
  }, [tariffLineDisplay, effectiveTariffRange]);

  const damLinePoints = useMemo(() => {
    return buildLinePoints(damLineDisplay);
  }, [damLineDisplay, effectiveTariffRange]);

  const miningTariffLinePoints = useMemo(() => {
    return buildLinePoints(miningTariffLineDisplay);
  }, [miningTariffLineDisplay, effectiveTariffRange]);

  const leftAxisTicks = useMemo(() => {
    const { min, max } = effectiveTariffRange;
    const ticks = [];
    for (let i = 0; i <= 4; i++) {
      ticks.push(min + (max - min) * (i / 4));
    }
    return ticks;
  }, [effectiveTariffRange]);

  /** 166 MWh = 100% for title percentage */
  const TOTAL_KWH_100_PCT = 166000;
  const totalKwh = useMemo(
    () =>
      (hourlyKwh.length ? hourlyKwh.reduce((a, b) => a + Number(b), 0) : 0) +
      (hourlyKwhToka.length ? hourlyKwhToka.reduce((a, b) => a + Number(b), 0) : 0),
    [hourlyKwh, hourlyKwhToka]
  );
  const pctOf166Mwh = totalKwh > 0 ? (totalKwh / TOTAL_KWH_100_PCT) * 100 : 0;

  const handleDateChange = useCallback(newYmd => {
    setDateValue(newYmd);
  }, []);

  useEffect(() => {
    if (embedded || typeof onSelectedDateChange !== 'function') return;
    onSelectedDateChange(dateValue);
  }, [embedded, dateValue, onSelectedDateChange]);

  return (
    <section className={cn(styles.chartSection, embedded && styles.chartSectionEmbedded)} aria-labelledby={titleId}>
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h2 id={titleId} className={styles.chartTitle}>
            <>
              {hasData && totalKwh > 0 && (
                <>
                  <span className={styles.chartTitlePct}>
                    {t('b2bDayKwhChartMarketShare', { pct: `${pctOf166Mwh.toFixed(1)}%` })}
                  </span>
                  <br />
                </>
              )}
              {t('b2bDayKwhChartTitle')}
            </>
          </h2>
          <DatePicker220
            value={dateValue}
            onChange={handleDateChange}
            max={formatDateForInput(new Date())}
            ariaLabel={t('b2bDayKwhChartDateLabel')}
          />
        </div>
        <div className={styles.chartWrap}>
          {isFetching && <div className={styles.emptyState}>{t('b2bDayKwhChartLoading')}</div>}
          {!isFetching && isRateLimited && <div className={styles.emptyState}>{t('b2bDayKwhChartRateLimited')}</div>}
          {!isFetching && !isRateLimited && hourlyKwh.length !== 24 && (
            <div className={styles.emptyState}>{t('b2bDayKwhChartNoData')}</div>
          )}
          {!isFetching && !isRateLimited && hourlyKwh.length === 24 && (
            <>
              <div className={styles.axesChartRow}>
                <div className={styles.leftYAxis} aria-hidden>
                  <div className={styles.yAxisTitle}>
                    <span className={styles.yAxisTitleText}>{t('b2bDayKwhChartTariffAxisLabel')}</span>
                  </div>
                  <div className={styles.yAxisTicks}>
                    {leftAxisTicks
                      .slice()
                      .reverse()
                      .map((val, idx) => (
                        <div key={idx} className={styles.yAxisTick}>
                          {val.toFixed(1)}
                        </div>
                      ))}
                  </div>
                </div>
                <div className={styles.chartAreaWrap}>
                  <div className={styles.barsWrap}>
                    {hourlyKwh.map((kwh, i) => {
                      const hour = i + 1;
                      const kwh220 = Number(kwh);
                      const kwhToka = Number(hourlyKwhToka[i] ?? 0);
                      const heightPct220 = maxKwh > 0 ? (kwh220 / maxKwh) * 100 : 0;
                      const heightPctToka = maxKwh > 0 ? (kwhToka / maxKwh) * 100 : 0;
                      return (
                        <div
                          key={hour}
                          className={styles.barCell}
                          onMouseEnter={() => setHoveredHour(i)}
                          onMouseLeave={() => setHoveredHour(null)}
                        >
                          <div className={styles.barGroup}>
                            <div
                              className={`${styles.bar} ${styles.bar220}`}
                              style={{
                                height: `${Math.max(heightPct220, 0)}%`,
                                minHeight: heightPct220 > 0 ? 2 : 0,
                              }}
                              role="img"
                              aria-label={`Hour ${hour} 220: ${kwh220.toFixed(1)} kWh`}
                            />
                            <div
                              className={`${styles.bar} ${styles.barToka}`}
                              style={{
                                height: `${Math.max(heightPctToka, 0)}%`,
                                minHeight: heightPctToka > 0 ? 2 : 0,
                              }}
                              role="img"
                              aria-label={`Hour ${hour} toka: ${kwhToka.toFixed(1)} kWh`}
                            />
                          </div>
                          <span className={styles.barLabel}>{hour}</span>
                        </div>
                      );
                    })}
                  </div>
                  <svg className={styles.tariffLineSvg} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                    {visibleSeries.evCharging && (
                      <polyline
                        points={tariffLinePoints}
                        fill="none"
                        stroke="rgba(255, 105, 180, 0.95)"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                    {visibleSeries.dam && (
                      <polyline
                        points={damLinePoints}
                        fill="none"
                        stroke="rgba(87, 214, 126, 0.95)"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                    {visibleSeries.mining && (
                      <polyline
                        points={miningTariffLinePoints}
                        fill="none"
                        stroke="rgba(246, 195, 67, 0.95)"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                  {hoveredHour != null && (
                    <div
                      className={styles.tooltip}
                      style={{
                        left: `${((hoveredHour + 0.5) / 24) * 100}%`,
                        top: -8,
                        transform: 'translate(-50%, -100%)',
                      }}
                    >
                      <div className={styles.tooltipRow}>
                        <span>{t('b2bDayKwhChartTooltipHour')}:</span>
                        <span>{String(hoveredHour + 1).padStart(2, '0')}:00</span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span>{t('b2bDayKwhChartLegend220')}:</span>
                        <span>
                          {Number(hourlyKwh[hoveredHour] ?? 0).toFixed(1)}
                          {t('energyKwh')}
                        </span>
                      </div>
                      <div className={styles.tooltipRow}>
                        <span>{t('b2bDayKwhChartLegendToka')}:</span>
                        <span>
                          {Number(hourlyKwhToka[hoveredHour] ?? 0).toFixed(1)}
                          {t('energyKwh')}
                        </span>
                      </div>
                      {visibleSeries.evCharging && (
                        <div className={styles.tooltipRow}>
                          <span>{t('b2bDayKwhChartSeriesEv')}:</span>
                          <span>
                            {Number(
                              Number(hourlyKwh[hoveredHour] ?? 0) + Number(hourlyKwhToka[hoveredHour] ?? 0) > 0
                                ? (hourlyTariffUahPerKwh[hoveredHour] ?? 0)
                                : 0
                            ).toFixed(2)}{' '}
                            {t('currency').trim()}/{t('energyKwh').trim()}
                          </span>
                        </div>
                      )}
                      {visibleSeries.dam && (
                        <div className={styles.tooltipRow}>
                          <span>{t('b2bDayKwhChartTooltipDam')}:</span>
                          <span>
                            {Number(hourlyDamUahPerKwh[hoveredHour] ?? 0).toFixed(2)} {t('currency').trim()}/
                            {t('energyKwh').trim()}
                          </span>
                        </div>
                      )}
                      {visibleSeries.mining && (
                        <div className={styles.tooltipRow}>
                          <span>{t('b2bDayKwhChartSeriesMining')}:</span>
                          <span>
                            {Number(hourlyMiningTariffUahPerKwh[hoveredHour] ?? 0).toFixed(2)} {t('currency').trim()}/
                            {t('energyKwh').trim()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.rightYAxis} aria-hidden>
                  <div className={styles.yAxisTitle}>
                    <span className={styles.yAxisTitleText}>{t('b2bDayKwhChartVolumeLabel')}</span>
                  </div>
                  <div className={styles.yAxisTicks}>
                    {rightAxisTicks
                      .slice()
                      .reverse()
                      .map((val, idx) => (
                        <div key={idx} className={styles.yAxisTick}>
                          {Number(val).toFixed(0)}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className={styles.legend}>
                <div className={styles.legendItem}>
                  <span className={styles.legendColor220} />
                  <span>{t('b2bDayKwhChartLegend220')}</span>
                </div>
                <div className={styles.legendItem}>
                  <span className={styles.legendColorToka} />
                  <span>{t('b2bDayKwhChartLegendToka')}</span>
                </div>
                {visibleSeries.evCharging && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggleActive}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, evCharging: !v.evCharging }))}
                  >
                    <span className={styles.legendTariff} />
                    <span>{t('b2bDayKwhChartSeriesEv')}</span>
                  </button>
                )}
                {!visibleSeries.evCharging && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggle}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, evCharging: !v.evCharging }))}
                  >
                    <span className={styles.legendTariff} />
                    <span>{t('b2bDayKwhChartSeriesEv')}</span>
                  </button>
                )}
                {visibleSeries.dam && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggleActive}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, dam: !v.dam }))}
                  >
                    <span className={styles.legendDam} />
                    <span>{t('b2bDayKwhChartSeriesDam')}</span>
                  </button>
                )}
                {!visibleSeries.dam && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggle}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, dam: !v.dam }))}
                  >
                    <span className={styles.legendDam} />
                    <span>{t('b2bDayKwhChartSeriesDam')}</span>
                  </button>
                )}
                {visibleSeries.mining && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggleActive}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, mining: !v.mining }))}
                  >
                    <span className={styles.legendMiningTariff} />
                    <span>{t('b2bDayKwhChartSeriesMining')}</span>
                  </button>
                )}
                {!visibleSeries.mining && (
                  <button
                    type="button"
                    className={`${styles.legendItem} ${styles.legendToggle}`}
                    onClick={() => setVisibleSeries(v => ({ ...v, mining: !v.mining }))}
                  >
                    <span className={styles.legendMiningTariff} />
                    <span>{t('b2bDayKwhChartSeriesMining')}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
